import { Component, OnInit, OnDestroy, Input, OnChanges, ChangeDetectionStrategy } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { Select, Store } from '@ngxs/store';
import { AuthState } from 'src/app/auth/state/auth/auth.state';
import { UserProfileModel } from 'src/app/auth/models/user-profile.model';
import { LocaleModel } from 'src/app/shared/models/locale.model';
import { ngxZendeskWebwidgetService } from 'ngx-zendesk-webwidget';
import { takeUntil, filter } from 'rxjs/operators';

@Component({
  selector: 'app-help-menu',
  templateUrl: './help-menu.component.html',
  styleUrls: ['./help-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HelpMenuComponent implements OnInit, OnDestroy, OnChanges {

  private ngUnsubscribe = new Subject();

  @Select(AuthState.getUserProfile)
  userProfile$: Observable<UserProfileModel>;

  @Select(AuthState.getUserLocale)
  userLocale$: Observable<LocaleModel>;

  @Input()
  widgetHorizontalPosition: 'left' | 'right' = 'right';

  @Input()
  widgetVerticalPosition: 'top' | 'bottom' = 'bottom';

  @Input()
  widgetHorizontalOffset = 0;

  @Input()
  widgetVerticalOffset = 0;

  constructor(
    private store: Store,
    private zendeskService: ngxZendeskWebwidgetService
  ) { }

  ngOnInit() {
    // global settings (position, etc...)
    this.updateZendeskWidgetSettings();

    // set settings
    this.zendeskService.setHelpCenterSuggestions({
      labels: ['webhelp']
    });

    // set identity
    this.userProfile$
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((profile) => {
        if (profile) {
          this.zendeskService.identify({
            name: this.getUserFullName(profile),
            email: profile.email
          });
          this.updateZendeskWidgetSettings();
        }
      });

    // set locale
    this.userLocale$
      .pipe(
        filter(userLocale => userLocale != null),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((userLocale) => {
        const locale = userLocale.lang;
        this.zendeskService.setLocale(locale);
      });
  }

  ngOnChanges() {
   this.updateZendeskWidgetSettings();
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  handleHelpClicked() {
   this.showZendeskWidget();
  }

  updateZendeskWidgetSettings() {
    const profile = this.store.selectSnapshot(AuthState.getUserProfile);    

    // generate custom user tags
    const userTags = [];
    if (profile) {
      const { premium_account, premium_account_type, developer_account, subscription } = profile;

      // permium user
      if (premium_account) {
        userTags.push(`premium_account_${premium_account_type}`);
        // subscription type
        if (subscription) {
          userTags.push(`subscription_${subscription.subscription_provider}`);
        }
      } else {
        userTags.push('free_account');
      }
      // developer
      if (developer_account) {
        userTags.push('developer_account');
      }
    }
    
    this.zendeskService.setSettings({
      // widget placement
      webWidget: {
        position: {
          horizontal: this.widgetHorizontalPosition,
          vertical: this.widgetVerticalPosition
        },
        offset: {
          horizontal: `${this.widgetHorizontalOffset}px`,
          vertical: `${this.widgetVerticalOffset}px`
        },
        contactForm: {
          tags: userTags,
          fields: [
            { id: 'name', prefill: { '*': this.getUserFullName(profile) } }
          ]
        }
      }
    });
  }

  showZendeskWidget() {
    this.zendeskService.activate({
      hideOnClose: true
    });
  }

  hideZendeskWidget() {
    this.zendeskService.hide();
  }

  private getUserFullName(profile: UserProfileModel): string {
    if (profile) {
      const { first_name, last_name } = profile;
      return `${first_name} ${last_name}`;
    } else {
      return '';
    }
  }

}
